export type ApiEnvelope<T> = {
  object?: string;
  attributes?: T;
  data?: Array<{ object: string; attributes: T }>;
  meta?: Record<string, unknown>;
  errors?: Array<{ code: string; detail: string }>;
  error?: string;
};

export async function apiEnvelope<T>(path: string, options: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const headers = new Headers(options.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'Application/vnd.pterodactyl.v1+json');
  if (options.body && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  const response = await fetch(path, { credentials: 'include', ...options, headers });
  const payload = await response.json().catch(() => ({})) as ApiEnvelope<T>;
  if (!response.ok) throw new Error(payload.errors?.map(error => error.detail).join('\n') || payload.error || `Request failed with ${response.status}`);
  return payload;
}

export async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const payload = await apiEnvelope<T>(path, options);
  return (payload.attributes ?? payload.data ?? payload) as T;
}

export async function apiList<T>(path: string, signal?: AbortSignal): Promise<T[]> {
  const payload = await apiEnvelope<T>(path, { signal });
  if (!Array.isArray(payload.data)) throw new Error('The server returned an invalid list response.');
  return payload.data.map(row => row.attributes);
}

export async function apiAllList<T>(path: string): Promise<T[]> {
  const rows: T[] = [];
  const url = new URL(path, window.location.origin);
  let page = 1;
  for (;;) {
    url.searchParams.set('page', String(page));
    const payload = await apiEnvelope<T>(url.pathname + url.search);
    if (!Array.isArray(payload.data)) throw new Error('The server returned an invalid list response.');
    rows.push(...payload.data.map(row => row.attributes));
    const pagination = payload.meta?.pagination as { total_pages?: number } | undefined;
    if (!pagination?.total_pages || page >= pagination.total_pages) return rows;
    page++;
  }
}
